// TMDB Data Caching Service
import { queuedSupabaseOperation } from '../utils/supabaseWrapper';

// In-memory cache for immediate access
const memoryCache = new Map();

// Batch cache operations to prevent conflicts
const cacheBatch = new Map(); // Pending cache operations
let batchTimeout = null;
const BATCH_DELAY = 1000; // Increased to 1 second for better batching

// Global coordination to prevent concurrent operations
let globalCacheOperation = null;
const pendingCacheChecks = new Map(); // Pending cache check promises

// Cache configuration
const CACHE_CONFIG = {
  // Cache duration in milliseconds (7 days)
  TTL: 7 * 24 * 60 * 60 * 1000,
  // Maximum number of items in memory cache
  MAX_MEMORY_CACHE_SIZE: 500,
  // Batch size for database operations
  BATCH_SIZE: 50
};

/**
 * Generate a cache key for a movie.
 *
 * A movie that knows its TMDB id is keyed by that id, which is unambiguous.
 * Title-and-year keys are the fallback, and they are not: "November" and
 * "November Criminals" are both 2017, and which one a title search returns
 * first depends on TMDB's popularity ranking on the day the cache expires.
 * That is how a correctly imported film turned into a different one a week
 * after import.
 */
export const getCacheKey = (title, year, tmdbId = null) => {
  if (tmdbId) return `id:${tmdbId}`;
  const normalizedTitle = String(title || '').toLowerCase().trim();
  return year ? `${normalizedTitle}:${year}` : normalizedTitle;
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (cachedItem) => {
  if (!cachedItem || !cachedItem.timestamp) return false;
  const age = Date.now() - cachedItem.timestamp;
  return age < CACHE_CONFIG.TTL;
};

/**
 * Clean up memory cache if it gets too large
 */
const cleanupMemoryCache = () => {
  if (memoryCache.size > CACHE_CONFIG.MAX_MEMORY_CACHE_SIZE) {
    // Remove oldest 20% of items
    const itemsToRemove = Math.floor(CACHE_CONFIG.MAX_MEMORY_CACHE_SIZE * 0.2);
    const entries = Array.from(memoryCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    for (let i = 0; i < itemsToRemove; i++) {
      memoryCache.delete(entries[i][0]);
    }
    
    console.log(`🧹 Cleaned up ${itemsToRemove} items from TMDB memory cache`);
  }
};

/**
 * Process batched cache operations with global coordination
 */
const processCacheBatch = async () => {
  if (cacheBatch.size === 0) return;
  
  // Prevent concurrent batch operations
  if (globalCacheOperation) {
    console.log('⚠️ Cache batch already in progress, skipping duplicate');
    return globalCacheOperation;
  }
  
  const batchEntries = Array.from(cacheBatch.values());
  cacheBatch.clear();
  batchTimeout = null;
  
  if (batchEntries.length === 0) return;
  
  console.log(`🎯 Processing coordinated TMDB cache batch: ${batchEntries.length} entries`);
  
  // Set global operation promise
  globalCacheOperation = (async () => {
    try {
      // Process entries one by one with individual conflict handling
      let successCount = 0;
      let conflictCount = 0;
      
      for (const entry of batchEntries) {
        try {
          await queuedSupabaseOperation.insert(
            'tmdb_cache',
            entry,
            null,
            `Cache: ${entry.title}`
          );
          successCount++;
        } catch (error) {
          if (error.message && error.message.includes('409')) {
            conflictCount++; // Expected - entry already exists
          } else {
            console.warn(`⚠️ Failed to cache ${entry.title}:`, error.message);
          }
        }
      }
      
      if (successCount > 0 || conflictCount > 0) {
        console.log(`✅ TMDB cache: ${successCount} new, ${conflictCount} existing, ${batchEntries.length} total`);
      }
      
      return { success: true, cached: successCount, existing: conflictCount };
    } catch (error) {
      console.warn(`⚠️ Batch TMDB cache failed:`, error.message);
      return { success: false, error };
    } finally {
      globalCacheOperation = null; // Reset global operation
    }
  })();
  
  return globalCacheOperation;
};

/**
 * Schedule batch processing
 */
const scheduleCacheBatch = () => {
  if (batchTimeout) return; // Already scheduled
  
  batchTimeout = setTimeout(processCacheBatch, BATCH_DELAY);
};

/**
 * Get cached TMDB data from memory first, then coordinated Supabase check
 */
export const getCachedTMDBData = async (title, year = null, tmdbId = null) => {
  const cacheKey = getCacheKey(title, year, tmdbId);
  
  // Check memory cache first (fastest)
  const memoryCached = memoryCache.get(cacheKey);
  if (memoryCached && isCacheValid(memoryCached)) {
    return memoryCached.data;
  }
  
  // Check if we're already fetching this cache entry
  if (pendingCacheChecks.has(cacheKey)) {
    console.log('⏳ Cache check already in progress for:', title);
    return pendingCacheChecks.get(cacheKey);
  }
  
  // Create coordinated cache check promise
  const cacheCheckPromise = (async () => {
    try {
      const data = await queuedSupabaseOperation.select(
        'tmdb_cache',
        `select=*&cache_key=eq.${encodeURIComponent(cacheKey)}`,
        null,
        `Check TMDB cache: ${title}`
      );
      
      const cacheResult = data && data.length > 0 ? data[0] : null;
      
      if (cacheResult && isCacheValid({ timestamp: new Date(cacheResult.cached_at).getTime() })) {
        // Store in memory cache for future access
        const cachedData = {
          data: cacheResult.tmdb_data,
          timestamp: new Date(cacheResult.cached_at).getTime()
        };
        memoryCache.set(cacheKey, cachedData);
        cleanupMemoryCache();
        
        return cacheResult.tmdb_data;
      }
      
      return null;
    } catch (error) {
      console.warn('⚠️ Cache check failed:', title, error.message);
      return null;
    } finally {
      pendingCacheChecks.delete(cacheKey);
    }
  })();
  
  // Store promise to prevent duplicate checks
  pendingCacheChecks.set(cacheKey, cacheCheckPromise);
  
  return cacheCheckPromise;
};

/**
 * Batch check multiple movies at once to reduce database requests
 */
export const batchCheckCache = async (movieTitles) => {
  console.log(`📋 Batch checking cache for ${movieTitles.length} movies...`);

  const cacheKeys = movieTitles.map(({ title, year, tmdbId }) => getCacheKey(title, year, tmdbId));
  const results = new Map();
  
  // Check memory cache first for all movies
  const uncachedKeys = [];
  const uncachedMovies = [];
  
  for (let i = 0; i < movieTitles.length; i++) {
    const { title, year } = movieTitles[i];
    const cacheKey = cacheKeys[i];
    const memoryCached = memoryCache.get(cacheKey);
    
    if (memoryCached && isCacheValid(memoryCached)) {
      results.set(cacheKey, memoryCached.data);
    } else {
      uncachedKeys.push(cacheKey);
      uncachedMovies.push({ title, year, cacheKey });
    }
  }
  
  console.log(`💾 Found ${results.size} movies in memory cache, checking ${uncachedKeys.length} in Supabase...`);
  
  // Batch check Supabase for remaining movies
  if (uncachedKeys.length > 0) {
    try {
      // Create a filter for multiple cache keys
      const keyFilter = uncachedKeys.map(key => `cache_key.eq.${encodeURIComponent(key)}`).join(',');
      
      const data = await queuedSupabaseOperation.select(
        'tmdb_cache',
        `select=*&or=(${keyFilter})`,
        null,
        `Batch check ${uncachedKeys.length} TMDB cache entries`
      );
      
      // Process results and update memory cache
      if (data && data.length > 0) {
        for (const cacheResult of data) {
          if (isCacheValid({ timestamp: new Date(cacheResult.cached_at).getTime() })) {
            const cachedData = {
              data: cacheResult.tmdb_data,
              timestamp: new Date(cacheResult.cached_at).getTime()
            };
            memoryCache.set(cacheResult.cache_key, cachedData);
            results.set(cacheResult.cache_key, cacheResult.tmdb_data);
          }
        }
        cleanupMemoryCache();
      }
      
      console.log(`✅ Batch cache check: ${data?.length || 0} found in Supabase`);
    } catch (error) {
      console.warn('⚠️ Batch cache check failed:', error.message);
    }
  }
  
  return results;
};

/**
 * Store TMDB data in both memory and Supabase cache
 */
export const setCachedTMDBData = async (title, year = null, tmdbData, tmdbId = null) => {
  if (!tmdbData) return;

  const cacheKey = getCacheKey(title, year, tmdbId);
  const timestamp = Date.now();
  
  // Store in memory cache immediately (UI gets instant access)
  memoryCache.set(cacheKey, {
    data: tmdbData,
    timestamp
  });
  cleanupMemoryCache();
  
  // Only add to batch if not already there (prevents duplicates)
  if (!cacheBatch.has(cacheKey)) {
    const cacheEntry = {
      cache_key: cacheKey,
      title: title,
      year: year,
      tmdb_data: tmdbData,
      cached_at: new Date().toISOString()
    };
    
    cacheBatch.set(cacheKey, cacheEntry);
    scheduleCacheBatch();
  }
  
  // No logging to reduce console spam
};

/**
 * Flush the cache batch immediately (useful for testing or when app is closing)
 */
export const flushCacheBatch = async () => {
  if (batchTimeout) {
    clearTimeout(batchTimeout);
    batchTimeout = null;
  }
  return processCacheBatch();
};

/**
 * Clear expired cache entries from Supabase
 */
export const cleanupExpiredCache = async () => {
  try {
    const expiredDate = new Date(Date.now() - CACHE_CONFIG.TTL).toISOString();
    
    await queuedSupabaseOperation.delete(
      'tmdb_cache',
      `cached_at=lt.${expiredDate}`,
      null, // No localStorage fallback for cleanup
      'Clean up expired TMDB cache entries'
    );
    
    console.log('🧹 Cleaned up expired TMDB cache entries');
  } catch (error) {
    console.warn('⚠️ Error cleaning up expired TMDB cache (non-critical):', error);
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = async () => {
  const memorySize = memoryCache.size;
  
  try {
    const data = await queuedSupabaseOperation.select(
      'tmdb_cache',
      'select=cache_key', // Just count rows
      null,
      'Get cache stats'
    );
    
    const supabaseSize = data ? data.length : 0;
    
    return {
      memorySize,
      supabaseSize,
      maxMemorySize: CACHE_CONFIG.MAX_MEMORY_CACHE_SIZE,
      ttlHours: CACHE_CONFIG.TTL / (60 * 60 * 1000)
    };
  } catch (error) {
    console.warn('Error getting cache stats:', error);
    return { memorySize, supabaseSize: 'unknown' };
  }
};

/**
 * Clear all cache data (for admin use)
 */
export const clearAllCache = async () => {
  // Clear memory cache
  memoryCache.clear();
  
  // Clear Supabase cache
  try {
    await queuedSupabaseOperation.delete(
      'tmdb_cache',
      'cache_key=neq.', // Delete all rows
      null,
      'Clear all TMDB cache'
    );
    
    console.log('✅ All TMDB cache cleared');
    return true;
  } catch (error) {
    console.warn('Error clearing TMDB cache:', error);
    return false;
  }
};

// Run cleanup on module load (once per session)
let cleanupScheduled = false;
if (!cleanupScheduled) {
  // Run cleanup after a short delay to avoid blocking app startup
  setTimeout(() => {
    cleanupExpiredCache();
  }, 5000);
  cleanupScheduled = true;
}

